import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import * as agentApi from '@/lib/agents/api';
import type { AgentConfig, CreateAgentInput } from '@/lib/agents/types';
import { Sparkles, Loader2, RefreshCw, Check, Edit, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface GeneratedConfig {
  name: string;
  description: string;
  agentType: string;
  config: AgentConfig;
  reasoning: string;
  suggestedImprovements?: string[];
}

export function PromptBuilder() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [description, setDescription] = useState('');
  const [generatedConfig, setGeneratedConfig] = useState<GeneratedConfig | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [error, setError] = useState<string | null>(null);

  const examplePrompts = [
    'A customer support agent that helps users with billing questions and can escalate issues',
    'A research assistant that finds information on topics and summarizes findings with citations',
    'A code review agent that analyzes PRs for bugs, security issues, and suggests improvements',
    'A task automation agent that breaks down complex workflows and executes them step by step',
  ];

  const handleGenerate = async () => {
    if (!description.trim()) {
      toast.error('Please describe your agent');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await agentApi.generateAgentConfig(description.trim());

      if (response.success && response.data) {
        setGeneratedConfig(response.data);
        setEditedName(response.data.name);
        toast.success('Configuration generated');
      } else {
        throw new Error('Failed to generate configuration');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate configuration';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefine = async () => {
    if (!refinementPrompt.trim() || !generatedConfig) {
      toast.error('Please provide refinement feedback');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await agentApi.refineAgentConfig(
        generatedConfig,
        refinementPrompt.trim()
      );

      if (response.success && response.data) {
        setGeneratedConfig(response.data);
        setRefinementPrompt('');
        toast.success('Configuration refined');
      } else {
        throw new Error('Failed to refine configuration');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refine configuration';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!generatedConfig) return;

    setCreating(true);
    setError(null);

    try {
      const input: CreateAgentInput = {
        name: isEditing ? editedName : generatedConfig.name,
        description: generatedConfig.description,
        agentType: generatedConfig.agentType as CreateAgentInput['agentType'],
        config: generatedConfig.config,
        isPublic: false,
        builderType: 'prompt',
      };

      const response = await agentApi.createAgent(input);
      toast.success('Agent created successfully');
      navigate({ to: `/agents/${response.data.id}` });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create agent';
      setError(message);
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Description Input */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            Describe Your Agent
          </CardTitle>
          <CardDescription>
            Tell us what you want your agent to do, and AI will generate the configuration for you.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="E.g., A research assistant that helps me find and summarize academic papers..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className="resize-none"
          />

          {/* Example prompts */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs">Try an example:</Label>
            <div className="flex flex-wrap gap-2">
              {examplePrompts.map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => setDescription(prompt)}
                  className="text-xs px-2 py-1 bg-muted rounded-md hover:bg-muted/80 transition-colors text-left"
                >
                  {prompt.slice(0, 50)}...
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleGenerate} disabled={loading || !description.trim()}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Config
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Generated Configuration */}
      {generatedConfig && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Generated Configuration</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Regenerate
                </Button>
              </CardTitle>
              {generatedConfig.reasoning && (
                <CardDescription>{generatedConfig.reasoning}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Agent Name */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Agent Name</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditing(!isEditing)}
                  >
                    <Edit className="h-3 w-3 mr-1" />
                    {isEditing ? 'Done' : 'Edit'}
                  </Button>
                </div>
                {isEditing ? (
                  <Input
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                  />
                ) : (
                  <p className="font-medium">{generatedConfig.name}</p>
                )}
              </div>

              {/* Config Summary */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Type</Label>
                  <p className="font-medium capitalize">{generatedConfig.agentType.replace('-', ' ')}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Provider</Label>
                  <p className="font-medium capitalize">{generatedConfig.config.llmProvider}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Model</Label>
                  <p className="font-medium">{generatedConfig.config.model || 'Default'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Temperature</Label>
                  <p className="font-medium">{generatedConfig.config.temperature}</p>
                </div>
              </div>

              {/* System Prompt */}
              <div className="space-y-2">
                <Label className="text-muted-foreground">System Prompt</Label>
                <div className="bg-muted p-3 rounded-md text-sm max-h-40 overflow-y-auto">
                  {generatedConfig.config.systemPrompt}
                </div>
              </div>

              {/* Tools */}
              {generatedConfig.config.tools && generatedConfig.config.tools.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Enabled Tools</Label>
                  <div className="flex flex-wrap gap-2">
                    {generatedConfig.config.tools.map((tool) => (
                      <span
                        key={tool}
                        className="px-2 py-1 bg-primary/10 text-primary rounded-md text-xs"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Persona */}
              {generatedConfig.config.persona && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Persona</Label>
                  <div className="text-sm">
                    {generatedConfig.config.persona.name && (
                      <span className="font-medium">{generatedConfig.config.persona.name}</span>
                    )}
                    {generatedConfig.config.persona.tone && (
                      <span className="text-muted-foreground ml-2">
                        ({generatedConfig.config.persona.tone})
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Suggested Improvements */}
              {generatedConfig.suggestedImprovements && generatedConfig.suggestedImprovements.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Suggested Improvements</Label>
                  <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                    {generatedConfig.suggestedImprovements.map((suggestion, i) => (
                      <li key={i}>{suggestion}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Refinement */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Refine Configuration</CardTitle>
              <CardDescription>
                Not quite right? Tell us what to change and AI will adjust.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="E.g., Make it more friendly, add web search capability, increase temperature..."
                value={refinementPrompt}
                onChange={(e) => setRefinementPrompt(e.target.value)}
                rows={2}
              />
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={handleRefine}
                  disabled={loading || !refinementPrompt.trim()}
                >
                  {loading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Refine
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Create Button */}
          <div className="flex justify-end">
            <Button onClick={handleCreate} disabled={creating} size="lg">
              {creating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Create Agent
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
